import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { bumpV2ReadEpoch } from '@/features/browserWallet/backend/readEpoch';
import {
    createV2TradeNotificationSource,
    type V2InvalidateScope,
} from '@/features/browserWallet/backend/tradeNotifications';
import {
    forgetClosedTradeHints,
    readLedgerHints,
} from '@/features/browserWallet/ledgerHints';
// deep import (not the feature barrel): the store does not reach back into the wallet feature.
import { useBrowserWalletStore } from '@/features/browserWallet/store';
import { queryKeys } from '@/lib/query';
import type { ConnectionState } from '@/lib/ws';

import type {
    TradeNotificationConnected,
    TradeNotificationError,
    TradeNotificationExecutionPhase,
    TradeNotificationUpdate,
} from '../types';

export interface UseV2TradeNotificationsOptions {
    /** True while the session key is a V2 one (`v2:<address>`); the hook is inert otherwise. */
    enabled: boolean;
    networkId: number;
    /** The session key, `v2:<address>` in V2. */
    token: string | null;
    onTradeUpdate?: (update: TradeNotificationUpdate) => void;
    onConnected?: (msg: TradeNotificationConnected) => void;
    onError?: (msg: TradeNotificationError) => void;
    onExecutionPhase?: (msg: TradeNotificationExecutionPhase) => void;
    onReconnect?: () => void;
}

export interface UseV2TradeNotificationsResult {
    connectionState: ConnectionState;
    isConnected: boolean;
    lastTradeUpdate: TradeNotificationUpdate | null;
}

type FeedConnectionState = 'connecting' | 'connected' | 'disconnected';

/**
 * The feed's state as the V1 socket reports it: a feed that was live and dropped (or was suspended with the page
 * hidden) is `reconnecting`, since the feed manager resubscribes on its own.
 */
function connectionStateOf(
    state: FeedConnectionState,
    everConnected: boolean,
): ConnectionState {
    if (state === 'connected') {
        return 'connected';
    }
    if (everConnected) {
        return 'reconnecting';
    }
    return state;
}

/**
 * The V2 (browser wallet) trade-notification source: Hyperliquid's `orderUpdates`, `userFills` and
 * `clearinghouseState` feeds for the unlocked wallet, mapped onto the same `TradeNotificationUpdate` messages the V1
 * socket sends (`backend/tradeNotifications.ts`), driving the same callbacks `useTradeNotifications` takes.
 *
 * Subscribes only when `enabled` and a signer exists, through the vendored feed manager (loaded with a dynamic
 * `import()` so the engine stays out of the V1 bundle); tears down on unmount and on a wallet change. Every push that
 * can change the account invalidates the position, positions, balance and trades queries, coalesced, after marking
 * the V2 backend's memoized reads stale. `onReconnect` fires when the feed is live again after a drop: fills pushed
 * meanwhile are not replayed, so the caller reconciles from the trades read. Nothing V2 sends maps to `onError` or
 * `onExecutionPhase` (no server is in the order path).
 */
export function useV2TradeNotifications(
    options: UseV2TradeNotificationsOptions,
): UseV2TradeNotificationsResult {
    const { enabled, networkId } = options;
    const signerAddress = useBrowserWalletStore(
        (s) => s.signer?.address ?? null,
    );
    const address = enabled ? signerAddress : null;
    // read through the context, not `useQueryClient()`: the hook always runs inside `useTradeNotifications`, also
    // where no provider is mounted (V1 tests), and it must stay inert there instead of throwing
    const queryClient = useContext(QueryClientContext);

    const callbacksRef = useRef(options);
    useEffect(() => {
        callbacksRef.current = options;
    });

    const [connectionState, setConnectionState] =
        useState<ConnectionState>('disconnected');
    const [lastTradeUpdate, setLastTradeUpdate] =
        useState<TradeNotificationUpdate | null>(null);

    useEffect(() => {
        if (address === null) {
            setConnectionState('disconnected');
            return;
        }
        let disposed = false;
        let teardown: (() => void) | null = null;
        setConnectionState('connecting');

        const invalidate = ({ account }: V2InvalidateScope) => {
            // the backend's memoized reads first, or the refetches below would be answered from them
            bumpV2ReadEpoch(address);
            if (!queryClient) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: [...queryKeys.speed.all, 'position'],
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.positions(),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.balance(),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.all,
            });
            if (account) {
                // a deposit landing is what funds the account: the activation status follows it
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                });
            }
        };

        void import('@snappy/core/lib/hl/feeds')
            .then(({ getFeedManager }) => {
                if (disposed) {
                    return;
                }
                const feed = getFeedManager();
                const source = createV2TradeNotificationSource({
                    networkId,
                    readHints: () => readLedgerHints(address),
                    emit: (update) => {
                        setLastTradeUpdate(update);
                        callbacksRef.current.onTradeUpdate?.(update);
                    },
                    invalidate,
                    forgetClosed: (coin, entryOid) =>
                        forgetClosedTradeHints(address, coin, entryOid),
                });

                let everConnected = false;
                let dropped = false;
                const apply = (state: FeedConnectionState) => {
                    if (disposed) {
                        return;
                    }
                    if (state === 'connected') {
                        setConnectionState('connected');
                        callbacksRef.current.onConnected?.({
                            type: 0,
                            userAddress: address,
                            networkId,
                        });
                        if (dropped) {
                            dropped = false;
                            callbacksRef.current.onReconnect?.();
                        }
                        everConnected = true;
                        return;
                    }
                    if (state === 'disconnected') {
                        source.disconnected();
                        dropped ||= everConnected;
                    }
                    setConnectionState(connectionStateOf(state, everConnected));
                };

                let last = feed.connectionState();
                const unsubscribeState = feed.onConnectionState((state) => {
                    if (state !== last) {
                        last = state;
                        apply(state);
                    }
                });
                const unsubscribeUser = feed.onUser(address, {
                    fills: source.fills,
                    orderUpdates: source.orderUpdates,
                    clearinghouse: source.clearinghouse,
                });
                last = feed.connectionState();
                apply(last);

                teardown = () => {
                    unsubscribeUser();
                    unsubscribeState();
                    source.dispose();
                };
            })
            .catch((error: unknown) => {
                console.warn('[V2TradeNotifications] feed unavailable', error);
                if (!disposed) {
                    setConnectionState('disconnected');
                }
            });

        return () => {
            disposed = true;
            teardown?.();
            teardown = null;
        };
    }, [address, networkId, queryClient]);

    return useMemo(() => {
        if (address === null) {
            return {
                connectionState: 'disconnected',
                isConnected: false,
                lastTradeUpdate: null,
            };
        }
        return {
            connectionState,
            isConnected: connectionState === 'connected',
            lastTradeUpdate,
        };
    }, [address, connectionState, lastTradeUpdate]);
}
